import { useState, useRef, useEffect, useCallback } from 'react'

interface MentionUser {
  id: number
  pseudo: string
  name: string
  specialty: string | null
}

interface MentionTextareaProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  className?: string
}

export function MentionTextarea({ id, value, onChange, placeholder, rows = 3, className }: MentionTextareaProps) {
  const [suggestions, setSuggestions] = useState<MentionUser[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mentionStart, setMentionStart] = useState<number | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 1) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    // Cancel previous request
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    try {
      const response = await fetch(`/mentions?q=${encodeURIComponent(query)}`, {
        signal: abortRef.current.signal,
        headers: { 'Accept': 'application/json' }
      })
      if (response.ok) {
        const users: MentionUser[] = await response.json()
        setSuggestions(users)
        setShowSuggestions(users.length > 0)
        setSelectedIndex(0)
      }
    } catch {
      // Aborted or network error — ignore
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart

    onChange(newValue)

    // Detect if we're in a mention context
    const textBeforeCursor = newValue.slice(0, cursorPos)
    const mentionMatch = textBeforeCursor.match(/@([\w\p{L}\p{M}]*)$/u)

    if (mentionMatch) {
      const start = cursorPos - mentionMatch[0].length
      setMentionStart(start)
      fetchSuggestions(mentionMatch[1])
    } else {
      setMentionStart(null)
      setShowSuggestions(false)
      setSuggestions([])
    }
  }

  const insertMention = (user: MentionUser) => {
    if (mentionStart === null || !textareaRef.current) return

    const before = value.slice(0, mentionStart)
    const afterCursor = value.slice(textareaRef.current.selectionStart)
    const mention = `@${user.pseudo} `
    const newValue = before + mention + afterCursor

    onChange(newValue)
    setShowSuggestions(false)
    setSuggestions([])
    setMentionStart(null)

    // Restore focus and cursor position
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const newCursorPos = before.length + mention.length
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
      }
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev + 1) % suggestions.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length)
        break
      case 'Enter':
      case 'Tab':
        e.preventDefault()
        insertMention(suggestions[selectedIndex])
        break
      case 'Escape':
        setShowSuggestions(false)
        break
    }
  }

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        id={id}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        rows={rows}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 bottom-full mb-1 left-0 w-full max-w-sm bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden"
        >
          {suggestions.map((user, index) => (
            <button
              key={user.id}
              type="button"
              onClick={() => insertMention(user)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors ${
                index === selectedIndex ? 'bg-tessan-primary/10 text-tessan-primary' : 'hover:bg-gray-50'
              }`}
            >
              <div className="w-7 h-7 rounded-full bg-tessan-primary text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                {user.pseudo[0]?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">{user.pseudo}</div>
                <div className="text-xs text-gray-500 truncate">
                  {user.name}
                  {user.specialty && ` · ${user.specialty}`}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
